/**
 * 图片压缩器 - 专门处理图片文件的真正压缩
 * 
 * 核心理念：
 * ✅ 保持图片可读性
 * ✅ 真正的质量压缩
 * ✅ 支持多种图片格式
 * ✅ 快速处理
 */

interface ImageCompressResult {
  success: boolean;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  blob: Blob;
  processingTime: number;
  method: string;
  originalFormat: string;
  compressedFormat: string;
  error?: string;
}

interface ImageCompressOptions {
  quality?: number;        // 0.1-1.0 图片质量
  maxWidth?: number;       // 最大宽度
  maxHeight?: number;      // 最大高度
  outputFormat?: string;   // 输出格式：'jpeg', 'webp', 'png'
  enableLog?: boolean;
}

export class ImageCompressor {
  
  /**
   * 🖼️ 图片压缩主方法
   */
  static async compress(
    file: File,
    options: ImageCompressOptions = {}
  ): Promise<ImageCompressResult> {
    const startTime = Date.now();
    const {
      quality = 0.8,
      maxWidth = 1920,
      maxHeight = 1080,
      outputFormat = 'jpeg',
      enableLog = true
    } = options;

    try {
      if (enableLog) {
        console.log(`🖼️ 开始图片压缩: ${file.name} (${(file.size / 1024).toFixed(2)}KB)`);
      }

      // 检查是否为图片文件
      if (!file.type.startsWith('image/')) {
        throw new Error('文件不是图片格式');
      }

      // 创建图片对象
      const img = await this.loadImage(file);
      
      // 计算压缩后的尺寸
      const { width, height } = this.calculateDimensions(
        img.width, 
        img.height, 
        maxWidth, 
        maxHeight
      );

      // 创建Canvas进行压缩
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('无法创建Canvas上下文');
      }

      // 绘制压缩后的图片
      ctx.drawImage(img, 0, 0, width, height);

      // 转换为压缩后的Blob
      const compressedBlob = await this.canvasToBlob(canvas, outputFormat, quality);
      
      const processingTime = Date.now() - startTime;
      
      const result: ImageCompressResult = {
        success: true,
        originalSize: file.size,
        compressedSize: compressedBlob.size,
        compressionRatio: compressedBlob.size / file.size,
        blob: compressedBlob,
        processingTime,
        method: 'Canvas压缩',
        originalFormat: file.type,
        compressedFormat: compressedBlob.type
      };

      if (enableLog) {
        const savings = ((1 - result.compressionRatio) * 100).toFixed(1);
        console.log(
          `✅ 图片压缩完成! ` +
          `${(result.originalSize / 1024).toFixed(2)}KB → ` +
          `${(result.compressedSize / 1024).toFixed(2)}KB ` +
          `(节省${savings}%, 耗时${processingTime}ms)`
        );
        console.log(`📐 尺寸: ${img.width}x${img.height} → ${width}x${height}`);
        console.log(`🎨 格式: ${file.type} → ${compressedBlob.type}`);
      }

      return result;

    } catch (error) {
      const processingTime = Date.now() - startTime;
      if (enableLog) {
        console.error('❌ 图片压缩失败:', error);
      }

      return {
        success: false,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        blob: new Blob([file], { type: file.type }),
        processingTime,
        method: '图片压缩',
        originalFormat: file.type,
        compressedFormat: file.type,
        error: error instanceof Error ? error.message : '未知错误'
      };
    }
  }

  /**
   * 📸 加载图片
   */
  private static loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('图片加载失败'));
      
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * 📐 计算压缩尺寸
   */
  private static calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    let { width, height } = { width: originalWidth, height: originalHeight };

    // 如果超出最大尺寸，按比例缩放
    if (width > maxWidth || height > maxHeight) {
      const aspectRatio = width / height;

      if (width > height) {
        width = maxWidth;
        height = width / aspectRatio;
      } else {
        height = maxHeight;
        width = height * aspectRatio;
      }
    }

    return {
      width: Math.round(width),
      height: Math.round(height)
    };
  }

  /**
   * 🎨 Canvas转Blob
   */
  private static canvasToBlob(
    canvas: HTMLCanvasElement,
    format: string,
    quality: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const mimeType = format === 'png' ? 'image/png' : 
                     format === 'webp' ? 'image/webp' : 'image/jpeg';
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Canvas转换失败'));
          }
        },
        mimeType,
        format === 'png' ? undefined : quality
      );
    });
  }

  /**
   * 🚀 智能图片压缩 - 根据文件大小自动调整
   */
  static async smartCompress(file: File): Promise<ImageCompressResult> {
    const fileSizeKB = file.size / 1024;
    
    let options: ImageCompressOptions;
    
    if (fileSizeKB <= 100) {
      // 小图片：轻度压缩，保持高质量
      options = {
        quality: 0.9,
        maxWidth: 1920,
        maxHeight: 1080,
        outputFormat: 'jpeg'
      };
    } else if (fileSizeKB <= 500) {
      // 中等图片：标准压缩
      options = {
        quality: 0.8,
        maxWidth: 1600,
        maxHeight: 900,
        outputFormat: 'jpeg'
      };
    } else if (fileSizeKB <= 2000) {
      // 大图片：重度压缩
      options = {
        quality: 0.7,
        maxWidth: 1280,
        maxHeight: 720,
        outputFormat: 'jpeg'
      };
    } else {
      // 超大图片：极限压缩
      options = {
        quality: 0.6,
        maxWidth: 1024,
        maxHeight: 576,
        outputFormat: 'jpeg'
      };
    }

    console.log(`🧠 智能图片压缩策略: 质量${(options.quality! * 100).toFixed(0)}%, 最大尺寸${options.maxWidth}x${options.maxHeight}`);
    
    return this.compress(file, options);
  }

  /**
   * 🔍 检查浏览器支持
   */
  static checkSupport(): {
    canvas: boolean;
    jpeg: boolean;
    png: boolean;
    webp: boolean;
  } {
    const canvas = document.createElement('canvas');
    
    return {
      canvas: !!canvas.getContext('2d'),
      jpeg: canvas.toDataURL('image/jpeg').startsWith('data:image/jpeg'),
      png: canvas.toDataURL('image/png').startsWith('data:image/png'),
      webp: canvas.toDataURL('image/webp').startsWith('data:image/webp')
    };
  }
} 